<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { ref } from "vue";

import { Button } from "@/components/ui/button/index";
import { usersPath } from "@/routes";

defineOptions({ inheritAttrs: false });
interface User {
  id: number;
  name: string;
  email: string;
  verified: boolean;
  created_at: string;
}

interface Pagy {
  count: number;
  page: number;
  pages: number;
  from: number;
  to: number;
}

interface Props {
  users: User[];
  pagy: Pagy;
  filters: {
    email_cont?: string;
  };
}

const props = defineProps<Props>();
const search = ref(props.filters?.email_cont ?? "");

const handleSearch = () => {
  router.get(
    usersPath(),
    search.value.trim() ? { q: { email_cont: search.value.trim() } } : {},
    { preserveState: true, replace: true },
  );
};

const clearSearch = () => {
  search.value = "";
  router.get(usersPath(), {}, { preserveState: true, replace: true });
};
</script>

<template>
  <Head title="Users" />

  <main class="page-container page-section">
    <section class="card-surface p-6">
      <header class="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 class="text-2xl font-semibold tracking-tight">Users</h1>
          <p class="text-sm text-muted-foreground">
            Manage registered accounts.
          </p>
        </div>
        <span
          class="rounded-md border bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"
        >
          {{ props.pagy.count }} total
        </span>
      </header>

      <div class="mb-6 flex flex-wrap items-center gap-2">
        <input
          v-model="search"
          type="search"
          placeholder="Search by email"
          class="input-field max-w-sm"
          @keyup.enter="handleSearch"
        />
        <Button
          type="button"
          @click="handleSearch"
        >
          Search
        </Button>
        <Button
          v-if="props.filters?.email_cont"
          type="button"
          variant="outline"
          @click="clearSearch"
        >
          Clear
        </Button>
      </div>

      <div class="overflow-x-auto rounded-xl border">
        <table class="w-full min-w-[680px] text-left text-sm">
          <thead
            class="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground"
          >
            <tr>
              <th class="px-3 py-2">ID</th>
              <th class="px-3 py-2">Name</th>
              <th class="px-3 py-2">Email</th>
              <th class="px-3 py-2">Verified</th>
              <th class="px-3 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="props.users.length === 0">
              <td
                colspan="5"
                class="px-3 py-8 text-center text-muted-foreground"
              >
                No users found.
              </td>
            </tr>
            <tr v-for="user in props.users" :key="user.id" class="border-t">
              <td class="px-3 py-2">{{ user.id }}</td>
              <td class="px-3 py-2">{{ user.name || "-" }}</td>
              <td class="px-3 py-2">{{ user.email }}</td>
              <td class="px-3 py-2">{{ user.verified ? "Yes" : "No" }}</td>
              <td class="px-3 py-2">
                {{ new Date(user.created_at).toLocaleDateString() }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer
        v-if="props.pagy.pages > 1"
        class="mt-4 flex justify-between text-xs text-muted-foreground"
      >
        <span
          >Showing {{ props.pagy.from }}-{{ props.pagy.to }} of
          {{ props.pagy.count }}</span
        >
        <span>Page {{ props.pagy.page }} / {{ props.pagy.pages }}</span>
      </footer>
    </section>
  </main>
</template>
